export type AnnotationTool = 'pan' | 'pen' | 'blur' | 'eraser' | 'measurement' | 'marker';
export type DrawingTool = 'pen';
export type StrokeMode = 'freehand' | 'line' | 'ellipse' | 'rectangle' | 'highlighter';
export type ImageRotation = 0 | 90 | 180 | 270;
export type MeasurementType = 'voltage' | 'resistance' | 'current' | 'capacitance' | 'inductance' | 'frequency' | 'temperature';
export type MeasurementUnit =
  | 'mV' | 'V'
  | 'Ω' | 'kΩ' | 'MΩ'
  | 'µA' | 'mA' | 'A'
  | 'pF' | 'nF' | 'µF'
  | 'nH' | 'µH' | 'mH'
  | 'Hz' | 'kHz' | 'MHz' | 'GHz';
export type MarkerSet = 'number' | 'alphabet' | 'text';

export interface AnnotationPoint {
  x: number;
  y: number;
  pressure?: number;
}

export interface StrokeAnnotation {
  id: string;
  type: 'stroke';
  tool: DrawingTool;
  color: string;
  width: number;
  opacity: number;
  points: AnnotationPoint[];
  /** Omitted by legacy JSON; an omitted mode is rendered as freehand. */
  mode?: StrokeMode;
}

export interface BlurAnnotation {
  id: string;
  type: 'blur';
  x: number;
  y: number;
  width: number;
  height: number;
  radius: number;
}

export interface MeasurementAnnotation {
  id: string;
  type: 'measurement';
  measurementType: MeasurementType;
  value: number;
  unit: MeasurementUnit;
  color: string;
  point: AnnotationPoint;
  labelPosition: AnnotationPoint;
  /** Clockwise label orientation in source-image coordinates. Omitted is equivalent to 0. */
  orientation?: ImageRotation;
}

export interface MarkerAnnotation {
  id: string;
  type: 'marker';
  markerSet: MarkerSet;
  value: number | string;
  text: string;
  color: string;
  point: AnnotationPoint;
  labelPosition: AnnotationPoint;
  /** Clockwise label orientation in source-image coordinates. Omitted is equivalent to 0. */
  orientation?: ImageRotation;
  /** @deprecated Read compatibility only. New JSON uses point and labelPosition. */
  position?: AnnotationPoint;
}

export interface LegacyMarkerAnnotation {
  id: string;
  type: 'marker';
  markerSet: MarkerSet;
  value: number | string;
  text: string;
  color: string;
  position: AnnotationPoint;
  point?: undefined;
  labelPosition?: undefined;
  orientation?: ImageRotation;
}

/** Future label/number tools can use this shape without changing the document envelope. */
export interface LabelAnnotation {
  id: string;
  type: 'label';
  text: string;
  color: string;
  point: AnnotationPoint;
}

export type ImageAnnotation = BlurAnnotation | StrokeAnnotation | MeasurementAnnotation | MarkerAnnotation | LegacyMarkerAnnotation | LabelAnnotation;

export interface ImageAnnotationDocument {
  version: 1;
  /** Clockwise display/export rotation. Omitted is equivalent to 0. */
  rotation?: ImageRotation;
  baseImage: {
    width: number;
    height: number;
  };
  annotations: ImageAnnotation[];
}

export interface AnnotationColor {
  label: string;
  value: string;
}

export interface AnnotationWidth {
  label: string;
  value: number;
}

export interface AnnotatedImageExportOptions {
  type?: 'image/jpeg' | 'image/png';
  quality?: number;
}

export interface MeasurementUnitDefinition {
  id: MeasurementUnit;
  label: string;
  category: MeasurementType;
  categoryLabel: string;
}

export interface MarkerDefinition {
  markerSet: MarkerSet;
  value: number | string;
  text: string;
}

export const MEASUREMENT_UNIT_DEFINITIONS: readonly MeasurementUnitDefinition[] = [
  { id: 'mV', label: 'mV', category: 'voltage', categoryLabel: '電圧' },
  { id: 'V', label: 'V', category: 'voltage', categoryLabel: '電圧' },
  { id: 'Ω', label: 'Ω', category: 'resistance', categoryLabel: '抵抗' },
  { id: 'kΩ', label: 'kΩ', category: 'resistance', categoryLabel: '抵抗' },
  { id: 'MΩ', label: 'MΩ', category: 'resistance', categoryLabel: '抵抗' },
  { id: 'µA', label: 'µA', category: 'current', categoryLabel: '電流' },
  { id: 'mA', label: 'mA', category: 'current', categoryLabel: '電流' },
  { id: 'A', label: 'A', category: 'current', categoryLabel: '電流' },
  { id: 'pF', label: 'pF', category: 'capacitance', categoryLabel: '容量' },
  { id: 'nF', label: 'nF', category: 'capacitance', categoryLabel: '容量' },
  { id: 'µF', label: 'µF', category: 'capacitance', categoryLabel: '容量' },
  { id: 'nH', label: 'nH', category: 'inductance', categoryLabel: 'インダクタンス' },
  { id: 'µH', label: 'µH', category: 'inductance', categoryLabel: 'インダクタンス' },
  { id: 'mH', label: 'mH', category: 'inductance', categoryLabel: 'インダクタンス' },
  { id: 'Hz', label: 'Hz', category: 'frequency', categoryLabel: '周波数' },
  { id: 'kHz', label: 'kHz', category: 'frequency', categoryLabel: '周波数' },
  { id: 'MHz', label: 'MHz', category: 'frequency', categoryLabel: '周波数' },
  { id: 'GHz', label: 'GHz', category: 'frequency', categoryLabel: '周波数' },
];

export const MARKER_DEFINITIONS: readonly MarkerDefinition[] = [
  ...['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨'].map((text, index) => ({
    markerSet: 'number' as const,
    value: index + 1,
    text,
  })),
  ...Array.from({ length: 26 }, (_, index) => ({
    markerSet: 'alphabet' as const,
    value: String.fromCharCode(65 + index),
    text: String.fromCharCode(65 + index),
  })),
];

export const DEFAULT_ANNOTATION_COLORS: readonly AnnotationColor[] = [
  { label: '赤', value: '#ff3b30' },
  { label: '黄', value: '#ffcc00' },
  { label: '緑', value: '#34c759' },
  { label: '青', value: '#007aff' },
  { label: '黒', value: '#111111' },
  { label: '白', value: '#ffffff' },
];

export const DEFAULT_ANNOTATION_WIDTHS: readonly AnnotationWidth[] = [
  { label: '細', value: 8 },
  { label: '中', value: 18 },
  { label: '太', value: 36 },
];
